import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gauge, Loader2, RotateCcw } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Common/Card';
import { Button } from '../Common/button';
import notify from '../../lib/notify';
import aiPromptService, { describeApiError } from '../../services/aiPromptService';
import organizationService from '../../services/organizationService';
import type { AnalysisLimits } from '../../types/projectAnalysis';
import { AnalysisLimitsTable, draftFrom, parseDraft, type LimitsDraft } from './AnalysisLimitsTable';

/**
 * Per-organization override of the analysis limits (God only). Empty fields
 * inherit the platform value; saved through the org ui-settings.
 */
export const OrganizationAnalysisLimitsCard: React.FC<{ organizationId: string }> = ({ organizationId }) => {
  const { t } = useTranslation();
  const [limits, setLimits] = useState<AnalysisLimits | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [draft, setDraft] = useState<LimitsDraft>({});
  const [errors, setErrors] = useState<Record<string, true>>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await aiPromptService.limits(organizationId);
      setLimits(data);
      setDraft(draftFrom(data.organization_values));
      setErrors({});
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, [organizationId]);

  useEffect(() => {
    setLimits(null);
    void load();
  }, [load]);

  const persist = async (values: Record<string, number> | null, done: string) => {
    setSaving(true);
    try {
      await organizationService.updateUiSettings(organizationId, { analysis_limits: values });
      await load();
      notify.success(done);
    } catch (error) {
      notify.error(describeApiError(error, t('projectAnalysisLimits.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (!limits) return;
    const parsed = parseDraft(draft, limits);
    setErrors(parsed.errors);
    if (Object.keys(parsed.errors).length) {
      notify.error(t('projectAnalysisLimits.invalid'));
      return;
    }
    void persist(Object.keys(parsed.values).length ? parsed.values : null, t('projectAnalysisLimits.saved'));
  };

  // Platform value an empty field falls back to.
  const inheritedOf = (key: string): number | undefined =>
    limits ? limits.global_values[key] ?? limits.defaults[key] : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Gauge className="h-4 w-4" />
          {t('projectAnalysisLimits.orgTitle')}
        </CardTitle>
        <CardDescription>{t('projectAnalysisLimits.orgDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadError ? (
          <p className="text-sm text-destructive">{t('projectAnalysisLimits.loadError')}</p>
        ) : !limits ? (
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        ) : (
          <>
            <AnalysisLimitsTable
              limits={limits}
              inheritedOf={inheritedOf}
              inheritedLabel={t('projectAnalysisLimits.columns.platform')}
              editor={{ draft, onChange: setDraft, errors, disabled: saving }}
            />
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => void persist(null, t('projectAnalysisLimits.orgCleared'))}
                disabled={saving || !Object.keys(limits.organization_values).length}
              >
                <RotateCcw className="h-4 w-4" />
                {t('projectAnalysisLimits.orgClear')}
              </Button>
              <Button size="sm" onClick={save} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('projectAnalysisLimits.save')}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OrganizationAnalysisLimitsCard;
